import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ArrowRight, Send, CheckCircle2 } from "lucide-react";
import { SeoHead } from "../../seo/SeoHead";
import { buildBreadcrumbListJsonLd } from "../../seo/site";
import { trackEvent } from "../../utils/analytics";

const SEO_DESCRIPTION =
  "Contactez Raguia : devis, démonstration, feedback ou autre demande. Formulaire sécurisé, réponse rapide.";

const MOTIFS = [
  { value: "", label: "— Choisir —", disabled: true },
  { value: "devis", label: "📋 Demande de devis" },
  { value: "demo", label: "🖥️ Démonstration" },
  { value: "feedback", label: "💬 Feedback produit" },
  { value: "partenariat", label: "🤝 Partenariat" },
  { value: "autre", label: "✉️ Autre demande" },
];

function motifFromSearchParams(searchParams) {
  const m = searchParams.get("motif") || searchParams.get("type");
  const allowed = new Set(["devis", "demo", "feedback", "partenariat", "autre"]);
  return m && allowed.has(m) ? m : "";
}

/** Signale un problème côté serveur (logs) sans bloquer l’utilisateur. */
function logContactIssueToServer(code, detail) {
  if (typeof fetch === "undefined") return;
  fetch("/api/public/client-log", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, detail }),
  }).catch(() => {});
}

function ContactForm({ initialMotif }) {
  const formRef = useRef(null);
  const [inquiryType, setInquiryType] = useState(initialMotif);
  const [status, setStatus] = useState({ text: "", ok: null });

  const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY ?? "";
  const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID ?? "";
  const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID ?? "";

  const configured =
    publicKey &&
    serviceId &&
    templateId &&
    !String(publicKey).startsWith("VOTRE_") &&
    !String(serviceId).startsWith("VOTRE_") &&
    !String(templateId).startsWith("VOTRE_");

  useEffect(() => {
    if (configured) emailjs.init({ publicKey });
  }, [configured, publicKey]);

  async function onSubmit(e) {
    e.preventDefault();
    setStatus({ text: "Envoi en cours…", ok: null });
    if (!configured) {
      const tech =
        "EmailJS non configuré dans le bundle : les variables VITE_EMAILJS_* sont injectées au moment du build Vite, pas depuis le .env du serveur. " +
        `État : publicKey=${publicKey ? "oui" : "non"}, serviceId=${serviceId ? "oui" : "non"}, templateId=${templateId ? "oui" : "non"}. ` +
        "En prod : redéployer le frontend après avoir défini ces variables dans CI (secrets) ou avant pnpm build.";
      console.error("[Contact]", tech);
      logContactIssueToServer("contact_emailjs_unconfigured", tech);
      setStatus({
        text:
          "Impossible d’envoyer le message pour le moment. Merci de réessayer plus tard ou de nous écrire directement à l’adresse indiquée sur le site.",
        ok: false,
      });
      return;
    }
    try {
      await emailjs.sendForm(serviceId, templateId, formRef.current);
      setStatus({ text: "Message envoyé. Merci !", ok: true });
      trackEvent("Contact", "Soumission_Formulaire", inquiryType || "Succès");
      formRef.current?.reset();
      setInquiryType("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[Contact] EmailJS sendForm failed:", err);
      logContactIssueToServer("contact_emailjs_send_failed", msg);
      setStatus({
        text:
          "L’envoi a rencontré un problème. Réessayez dans quelques minutes ou contactez-nous par e-mail.",
        ok: false,
      });
    }
  }

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      className="mt-10 space-y-5 rounded-[1.75rem] border-2 border-[#010101]/15 bg-white p-5 shadow-lg sm:p-8"
    >
      <div>
        <label htmlFor="inquiry_type" className="block text-sm font-medium text-[#010101]">
          Motif <span className="text-[#A43032]">*</span>
        </label>
        <select
          id="inquiry_type"
          name="inquiry_type"
          required
          value={inquiryType}
          onChange={(e) => setInquiryType(e.target.value)}
          className="mt-1.5 w-full rounded-xl border-2 border-[#010101]/20 bg-white px-4 py-3 text-sm text-[#010101] outline-none focus:border-[#A43032] focus:ring-2 focus:ring-[#A43032]/25"
        >
          {MOTIFS.map((o) => (
            <option key={o.value || "empty"} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="full_name" className="block text-sm font-medium text-[#010101]">
            Nom et prénom <span className="text-[#A43032]">*</span>
          </label>
          <input
            id="full_name"
            name="full_name"
            type="text"
            required
            autoComplete="name"
            placeholder="Jean Dupont"
            className="mt-1.5 w-full rounded-xl border-2 border-[#010101]/20 px-4 py-3 text-sm text-[#010101] outline-none focus:border-[#A43032] focus:ring-2 focus:ring-[#A43032]/25"
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="company" className="block text-sm font-medium text-[#010101]">
            Entreprise <span className="font-normal text-stone-500">(optionnel)</span>
          </label>
          <input
            id="company"
            name="company"
            type="text"
            autoComplete="organization"
            placeholder="SARL Exemple"
            className="mt-1.5 w-full rounded-xl border-2 border-[#010101]/20 px-4 py-3 text-sm text-[#010101] outline-none focus:border-[#A43032] focus:ring-2 focus:ring-[#A43032]/25"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-[#010101]">
            E-mail professionnel <span className="text-[#A43032]">*</span>
          </label>
<Input
              id="email"
              type="email"
              placeholder="votre@email.fr"
              required
              className="focus:ring-[#A43032]/25"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Input
              id="message"
              placeholder="Décrivez votre projet..."
              required
              className="focus:ring-[#A43032]/25"
            />
          </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-[#010101]">
            Téléphone <span className="font-normal text-stone-500">(optionnel)</span>
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            placeholder="+33 …"
            className="mt-1.5 w-full rounded-xl border-2 border-[#010101]/20 px-4 py-3 text-sm text-[#010101] outline-none focus:border-[#A43032] focus:ring-2 focus:ring-[#A43032]/25"
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="role" className="block text-sm font-medium text-[#010101]">
            Fonction <span className="font-normal text-stone-500">(optionnel)</span>
          </label>
          <input
            id="role"
            name="role"
            type="text"
            autoComplete="organization-title"
            placeholder="Responsable IT, DG…"
            className="mt-1.5 w-full rounded-xl border-2 border-[#010101]/20 px-4 py-3 text-sm text-[#010101] outline-none focus:border-[#A43032] focus:ring-2 focus:ring-[#A43032]/25"
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="message" className="block text-sm font-medium text-[#010101]">
            Message <span className="text-[#A43032]">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={6}
            placeholder="Contexte, délais, volume documentaire, nombre d’utilisateurs…"
            className="mt-1.5 w-full resize-y rounded-xl border-2 border-[#010101]/20 px-4 py-3 text-sm text-[#010101] outline-none focus:border-[#A43032] focus:ring-2 focus:ring-[#A43032]/25"
          />
        </div>
      </div>

        <p className="text-xs leading-relaxed text-stone-800">
        En envoyant ce formulaire, vous acceptez le traitement de vos données pour répondre
        à votre demande, conformément à notre{" "}
        <Link to="/confidentialite" className="font-medium text-[#A43032] hover:underline">
          politique de confidentialité
        </Link>
        .
      </p>

      <Button
        type="submit"
        variant="brand"
        size="lg"
        className="w-full sm:w-auto"
        disabled={status.type === "loading"}
      >
        <Send className="size-4" />
        Envoyer
      </Button>
      {status.text && (
        <div
          role="status"
          className={`rounded-2xl border px-4 py-3 text-sm leading-relaxed ${
            status.ok === true
              ? "border-emerald-200 bg-emerald-50/90 text-emerald-900"
              : status.ok === false
                ? "border-[#A43032]/25 bg-[#fff7f7] text-[#5c1f21]"
                : "border-stone-200 bg-stone-50 text-stone-700"
          }`}
        >
          {status.text}
        </div>
      )}
    </form>
  );
}

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const initialMotif = useMemo(
    () => motifFromSearchParams(searchParams),
    [searchParams],
  );

  return (
    <>
      <SeoHead
        title="Contact — Raguia"
        description={SEO_DESCRIPTION}
        jsonLd={buildBreadcrumbListJsonLd([
          { name: "Accueil", path: "/" },
          { name: "Contact", path: "/contact" },
        ])}
      />
      <main className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
      <p className="text-sm font-semibold uppercase tracking-wide text-[#A43032]">
        Contact
      </p>
      <h1 className="font-heading mt-2 text-3xl font-semibold tracking-tight text-[#010101] sm:text-4xl">
        📬 Parlons de votre projet
      </h1>
      <p className="mt-4 text-base leading-relaxed text-stone-800 sm:text-lg">
        Un seul formulaire : choisissez le motif, détaillez votre besoin — réponse sous les
        meilleurs délais.
      </p>

      <ContactForm key={searchParams.toString()} initialMotif={initialMotif} />
    </main>
    </>
  );
}
